import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AuthError, completeReset, requestReset, verifyResetCode } from '@radiology/api-client';
import { AuthLayout } from './AuthLayout';

type Step =
  | { name: 'identify' }
  | { name: 'code'; email: string }
  | { name: 'password'; resetTicket: string }
  | { name: 'done' };

const MIN_LENGTH = 12;

/** Mirrors the server-side rules in auth-reset. The server is the authority. */
function passwordProblems(password: string): string[] {
  const problems: string[] = [];
  if (password.length < MIN_LENGTH) problems.push(`at least ${MIN_LENGTH} characters`);
  if (!/[a-z]/.test(password) || !/[A-Z]/.test(password)) problems.push('upper and lower case');
  if (!/[0-9]/.test(password)) problems.push('a number');
  return problems;
}

/**
 * Password reset (decision #12).
 *
 * Four steps: identify, code, new password, done. Completing it does NOT sign
 * the user in — they go back to sign-in and use the new password plus a fresh
 * code. A reset often follows a compromise, so the new credential is exercised
 * through the whole flow rather than handed a session.
 *
 * `forcedEmail` is set for the 366 migrated users, who cannot reach the app
 * until they reset.
 */
export function ResetScreen({ onDone, forcedEmail }: { onDone: () => void; forcedEmail?: string }) {
  const [step, setStep] = useState<Step>(forcedEmail ? { name: 'identify' } : { name: 'identify' });
  const [identifier, setIdentifier] = useState(forcedEmail ?? '');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const problems = passwordProblems(password);
  const strength = Math.max(0, 3 - problems.length);

  const submitIdentify = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await requestReset(identifier.trim());
      // Always advances, whether or not the account exists — the server will not
      // say, and neither will we.
      setStep({ name: 'code', email: identifier.trim() });
    } catch (err) {
      setError(err instanceof AuthError ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step.name !== 'code') return;
    setBusy(true);
    setError(null);
    try {
      const ticket = await verifyResetCode(step.email, code.trim());
      setStep({ name: 'password', resetTicket: ticket });
    } catch (err) {
      setError(err instanceof AuthError ? err.message : 'That code was not accepted.');
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step.name !== 'password') return;
    setBusy(true);
    setError(null);
    try {
      await completeReset(step.resetTicket, password);
      setStep({ name: 'done' });
    } catch (err) {
      setError(err instanceof AuthError ? err.message : 'Could not set that password.');
    } finally {
      setBusy(false);
    }
  };

  if (step.name === 'done') {
    return (
      <AuthLayout
        title="Password updated"
        description="Sign in with your new password. You'll get a fresh code by email."
      >
        <Button variant="contained" size="large" fullWidth onClick={onDone}>
          Go to sign in
        </Button>
      </AuthLayout>
    );
  }

  if (step.name === 'password') {
    const mismatch = confirm.length > 0 && confirm !== password;
    return (
      <AuthLayout title="Choose a new password">
        <form onSubmit={e => void submitPassword(e)}>
          <Stack gap={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="New password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoFocus
              autoComplete="new-password"
              helperText={problems.length ? `Needs ${problems.join(', ')}.` : 'Looks good.'}
              error={password.length > 0 && problems.length > 0}
            />
            {password.length > 0 && (
              <LinearProgress
                variant="determinate"
                value={(strength / 3) * 100}
                color={strength === 3 ? 'success' : strength === 2 ? 'warning' : 'error'}
                sx={{ height: 4, borderRadius: 2, mt: -1 }}
              />
            )}
            <TextField
              label="Confirm password"
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              autoComplete="new-password"
              error={mismatch}
              helperText={mismatch ? 'The passwords do not match.' : ' '}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={busy || problems.length > 0 || password !== confirm}
            >
              {busy ? 'Saving…' : 'Set password'}
            </Button>
          </Stack>
        </form>
      </AuthLayout>
    );
  }

  if (step.name === 'code') {
    return (
      <AuthLayout
        title="Enter your code"
        description="If that account exists, we've emailed a 6-digit code. It expires in 5 minutes."
      >
        <form onSubmit={e => void submitCode(e)}>
          <Stack gap={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="6-digit code"
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              slotProps={{
                htmlInput: {
                  maxLength: 6,
                  style: { letterSpacing: '0.4em', fontSize: '1.15rem', fontWeight: 600 },
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={busy || code.length < 6}
            >
              {busy ? 'Checking…' : 'Continue'}
            </Button>
          </Stack>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={forcedEmail ? 'Set a new password' : 'Reset your password'}
      description={
        forcedEmail
          ? 'The portal has moved to a new sign-in system, so everyone needs to set a new password once. We’ll email you a code to confirm it’s you.'
          : 'Enter your username or work email and we’ll send a code.'
      }
      footer={
        !forcedEmail && (
          <Typography variant="caption" color="text.secondary">
            Remembered it?{' '}
            <Link component="button" type="button" variant="caption" onClick={onDone}>
              Back to sign in
            </Link>
            .
          </Typography>
        )
      }
    >
      <form onSubmit={e => void submitIdentify(e)}>
        <Stack gap={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Username or email"
            value={identifier}
            onChange={e => setIdentifier(e.target.value)}
            autoFocus
            disabled={Boolean(forcedEmail)}
            required
          />
          <Button type="submit" variant="contained" size="large" disabled={busy || !identifier}>
            {busy ? 'Sending…' : 'Send code'}
          </Button>
        </Stack>
      </form>
    </AuthLayout>
  );
}
